import { useMemo } from "react";
import { useSelector } from "react-redux";
import { hatRecht, RECHT_SCHREIBEN, getArtistMapWithEinnahmenBearbeiten, getArtistMapWithSchreibrecht } from "../models/permissions";
import { selectLoggedInArtistID, selectLoggedInIsAdmin, selectLoggedInArtist } from "../store/auth/authSelector";
import { selectArtistMap } from "../store/common/commonSelector";
import { useArtistRechteData } from "../components/artist/hooks/useArtistRechteData";

// Stable empty references for deny-by-default paths (prevent unnecessary re-renders)
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const OWN_FULL_RIGHTS = 15;

/**
 * Normalizes backend rights into a map keyed by the artist whose data may be accessed.
 *
 * Backend delivers: { VonArtistID, AnArtistID, Rechte, EinnBearbeiten }
 * - VonArtistID = the artist who GRANTED the right (whose data can be accessed)
 * - AnArtistID  = the artist who RECEIVED the right (the logged-in artist)
 *
 * The resulting map is keyed by VonArtistID and contains
 * { ArtistID, Rechte, EinnBearbeiten }.
 * The logged-in artist always receives full rights over their own data.
 */
const normalizeRights = (rechte, loggedInArtistId) => {
    const map = {};
    (rechte || []).forEach((item) => {
        if (item?.AnArtistID === loggedInArtistId && item?.VonArtistID) {
            map[item.VonArtistID] = {
                ArtistID: item.VonArtistID,
                Rechte: item.Rechte,
                EinnBearbeiten: Boolean(item.EinnBearbeiten),
            };
        }
    });
    if (loggedInArtistId) {
        map[loggedInArtistId] = {
            ArtistID: loggedInArtistId,
            Rechte: OWN_FULL_RIGHTS,
            EinnBearbeiten: true,
        };
    }
    return map;
};

/**
 * Query-backed rights hook for logged in artist.
 * Rights access is deny-by-default while query is not ready or failed.
 * @returns {Object}
 */
export const useLoggedInArtistRights = () => {
    const artistMap = useSelector(selectArtistMap);
    const loggedInArtistId = useSelector(selectLoggedInArtistID);
    const loggedInArtist = useSelector(selectLoggedInArtist);
    const isAdmin = useSelector(selectLoggedInIsAdmin);
    const rightsQuery = useArtistRechteData(loggedInArtistId);

    const isReady = Boolean(loggedInArtistId) && rightsQuery.isSuccess;
    const rechte = rightsQuery.data?.rechte;

    const rightsMap = useMemo(() => {
        if (!isReady) return EMPTY_OBJECT;
        return normalizeRights(rechte, loggedInArtistId);
    }, [isReady, rechte, loggedInArtistId]);

    const viewableArtistIdList = useMemo(() => {
        if (!isReady) return EMPTY_ARRAY;
        return Object.keys(rightsMap).map((id) => Number(id));
    }, [isReady, rightsMap]);

    const writableArtistIdList = useMemo(() => {
        if (!isReady) return EMPTY_ARRAY;
        return Object.values(rightsMap)
            .filter((right) => hatRecht(right, [RECHT_SCHREIBEN]))
            .map((right) => right.ArtistID);
    }, [isReady, rightsMap]);

    const artistMapWithViewRights = useMemo(() => {
        if (!isReady || !artistMap || !loggedInArtistId) return EMPTY_OBJECT;
        if (isAdmin) return artistMap;
        return viewableArtistIdList.reduce((acc, artistId) => {
            if (artistMap[artistId]) {
                acc[artistId] = artistMap[artistId];
            }
            return acc;
        }, {});
    }, [isReady, artistMap, loggedInArtistId, isAdmin, viewableArtistIdList]);

    const artistMapWithWriteRights = useMemo(() => {
        if (!isReady || !artistMap || !loggedInArtist) return EMPTY_OBJECT;
        return getArtistMapWithSchreibrecht(artistMap, loggedInArtist, rightsMap);
    }, [isReady, artistMap, loggedInArtist, rightsMap]);

    const artistMapWithEinnahmenRights = useMemo(() => {
        if (!isReady || !artistMap || !loggedInArtist) return EMPTY_OBJECT;
        return getArtistMapWithEinnahmenBearbeiten(artistMap, loggedInArtist, rightsMap);
    }, [isReady, artistMap, loggedInArtist, rightsMap]);

    return {
        isAdmin,
        isReady,
        isLoading: rightsQuery.isLoading || rightsQuery.isFetching,
        isError: rightsQuery.isError,
        error: rightsQuery.error,
        rightsMap,
        viewableArtistIdList,
        writableArtistIdList,
        artistMapWithViewRights,
        artistMapWithWriteRights,
        artistMapWithEinnahmenRights,
        refetch: rightsQuery.refetch,
    };
};
